import type { FC } from "react";

import {
  useGetFPSLAnalytics,
  useGetFPSLStatus,
} from "src/v2/api/hooks/useGetFPSL";
import {
  useGetHYCAccountStatus,
  useGetHYCAccruedInterest,
} from "src/v2/api/hooks/useGetHighYieldCash";
import type { Account, Margin } from "src/v2/api/rest/account";
import { useAccountContext } from "src/v2/providers/AccountProvider";
import { getTotalAccruedInterest } from "../../../highYieldCash/util";
import {
  ProgramsContainerPres,
  type ProgramsContainerPresProps,
} from "./ProgramsContainer.pres";

interface ProgramsContainerProps {
  account: Account;
  margin?: Margin | null;
  isMarginLoading?: boolean;
  onBalancesClick?: () => void;
  onInterestPaidClick?: () => void;
}

export const ProgramsContainer: FC<ProgramsContainerProps> = ({
  account,
  margin,
  isMarginLoading,
  onBalancesClick,
  onInterestPaidClick,
}) => {
  const { product } = useAccountContext();

  const { hycStatus, isLoading: isHycStatusLoading } = useGetHYCAccountStatus({
    accountID: account.id,
    options: {
      enabled: product === "live",
    },
  });

  const isEnrolled = hycStatus === "ACTIVE" || hycStatus === "PENDING_CHANGE";

  const { status: fpslStatus, isLoading: isFpslStatusLoading } =
    useGetFPSLStatus({
      accountID: account.id,
      product,
      options: { enabled: true },
    });

  const {
    data: hycAccruedInterest = [],
    isLoading: isHYCAccruedInterestLoading,
  } = useGetHYCAccruedInterest({
    accountID: account.id,
    options: { enabled: isEnrolled },
  });

  const { analytics: fpslAnalytics, isLoading: isFPSLAnalyticsLoading } =
    useGetFPSLAnalytics({
      accountID: account?.id,
      product,
      options: {
        enabled: !!account?.id && fpslStatus?.status === "ACTIVE",
      },
    });

  const customerInterest = fpslAnalytics?.interest?.customer ?? 0;

  const hycInterest = getTotalAccruedInterest({
    hycAccruedInterest,
  });

  const { last_equity, effective_buying_power, cash } = {
    cash: Number(margin?.cash),
    last_equity: Number(margin?.last_equity),
    effective_buying_power: Number(margin?.effective_buying_power),
  };

  const dailyChange = Number(margin?.equity) - last_equity;

  const programContainerProps: ProgramsContainerPresProps = {
    buyingPower: effective_buying_power,
    cash: cash,
    dailyChange: dailyChange,
    stockLending: customerInterest,
    highYieldCash: hycInterest,
    isBalancesLoading: isMarginLoading,
    isStockLendingLoading: isFpslStatusLoading || isFPSLAnalyticsLoading,
    isHighYieldCashLoading: isHycStatusLoading || isHYCAccruedInterestLoading,
  };

  return (
    <ProgramsContainerPres
      {...programContainerProps}
      onBalancesClick={onBalancesClick}
      onInterestPaidClick={onInterestPaidClick}
    />
  );
};

export default ProgramsContainer;
